import * as React from 'react'

import {
  TInputOrderRecipeSideDishCreate,
  TMAddProductToCartVariables,
  useMAddProductToCart,
} from '~graphql/generated/clientGraphql'

import { TProduct } from '../types'

type TProps = {
  /**
   * The ID of the order to which the product should be added.
   */
  orderId: NullableID
}

type TAddProductToCartOptions = {
  // product that should be added to the cart
  product: TProduct
  // addons that should be added to the product
  addons: RoA<TInputOrderRecipeSideDishCreate>
  // note from the customer about the product
  note: TMAddProductToCartVariables['note']
  // amount of the product that should be added to the cart
  amount: TMAddProductToCartVariables['amount']
  // promo tag of the product that is downloaded from the BE
  promoTag: TMAddProductToCartVariables['promoTag']
  // category id where the product is located
  categoryId: TMAddProductToCartVariables['categoryId']
}

/**
 * Hook for adding a product to the cart that uses the MAddProductToCart mutation.
 */
export const useAddProductToCart = ({ orderId }: TProps) => {
  const [mAddProductToCart, addResult] = useMAddProductToCart()

  /**
   * Adds a product to the cart.
   */
  const addProductToCart = React.useCallback(
    ({ amount, addons, product, note, categoryId, promoTag }: TAddProductToCartOptions) => {
      // if the order is still loading the orderId can be null
      // in such case, this shouldn’t be called at all (guarded in the UI)
      // but if the implementation changes in the future, do we want to add some DEV error message?
      if (!orderId) {
        return
      }

      mAddProductToCart({
        variables: {
          note: note,
          amount: amount,
          productId: product.id,
          orderId: orderId,
          addons,
          categoryId,
          promoTag,
        },
      })
    },
    [mAddProductToCart, orderId],
  )

  return { addProductToCart, addResult }
}
